import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, CircularProgress, Alert, Chip, Stack, Switch, TextField,
  Button, Paper, IconButton, useTheme, alpha,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getCriterioImpacto, guardarEvaluacionImpacto } from "../../http/actions/DashboardImpactoAction";

const colorHexFromNombre = (color) => {
  switch ((color || "").toLowerCase()) {
    case "rojo": return "#E53935";
    case "amarillo": return "#FB8C00";
    case "verde": return "#43A047";
    default: return "#9E9E9E";
  }
};

const nivelImpactoTexto = (color) => {
  switch ((color || "").toLowerCase()) {
    case "rojo": return "Impacto alto";
    case "amarillo": return "Impacto medio";
    case "verde": return "Impacto bajo";
    default: return "Sin evaluar";
  }
};

const formatFechaHora = (value) =>
  value ? new Date(value).toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "";

const colorDesdePuntaje = (pts) => (pts >= 4 ? "Rojo" : pts >= 2 ? "Amarillo" : "Verde");

const CriteriosImpactoContenido = ({
  token, idUsuario, idNormativa, esAdmin = false, onGuardado,
}) => {
  const theme = useTheme();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [borrador, setBorrador] = useState([]);

  const puedeEditar = true;

  const cargar = useCallback(() => {
    if (!idNormativa) return;
    setLoading(true);
    setError("");
    setEditando(false);
    getCriterioImpacto(token, idNormativa, esAdmin ? null : idUsuario)
      .then((res) => setData(res?.item || null))
      .catch((e) => { setData(null); setError(typeof e === "string" ? e : "No se pudo cargar el detalle de impacto."); })
      .finally(() => setLoading(false));
  }, [idNormativa, token, esAdmin, idUsuario]);

  useEffect(() => { cargar(); }, [cargar]);

  const puntajeBorrador = borrador.filter((p) => p.cumple).length;
  const colorBorrador = colorDesdePuntaje(puntajeBorrador);

  const iniciarEdicion = () => {
    setBorrador((data?.parametros || []).map((p) => ({
      parametro: p.parametro,
      pregunta: p.pregunta,
      cumple: p.cumple,
      justificacion: p.justificacion || "",
      esLibre: esAdmin ? !!p.esLibre : true,
    })));
    setEditando(true);
    setError("");
  };

  const cancelarEdicion = () => {
    setEditando(false);
    setBorrador([]);
    setError("");
  };

  const toggleCumple = (idx) =>
    setBorrador((prev) => prev.map((p, i) => (i === idx ? { ...p, cumple: !p.cumple } : p)));

  const cambiarJustificacion = (idx, valor) =>
    setBorrador((prev) => prev.map((p, i) => (i === idx ? { ...p, justificacion: valor } : p)));

  const cambiarTitulo = (idx, valor) =>
    setBorrador((prev) => prev.map((p, i) => (i === idx ? { ...p, pregunta: valor } : p)));

  const agregarCriterio = () =>
    setBorrador((prev) => [
      ...prev,
      { parametro: "", pregunta: "", cumple: false, justificacion: "", esLibre: true },
    ]);

  const eliminarCriterio = (idx) =>
    setBorrador((prev) => prev.filter((_, i) => i !== idx));

  const guardar = () => {
    const limpios = borrador
      .map((p) => {
        const nombre = p.esLibre ? (p.pregunta || "").trim() : (p.parametro || "").trim();
        return { ...p, parametro: nombre };
      })
      .filter((p) => p.parametro.length > 0);

    const nombres = limpios.map((p) => p.parametro.toLowerCase());
    if (new Set(nombres).size !== nombres.length) {
      setError("Hay criterios con el mismo nombre. Renómbralos antes de guardar.");
      return;
    }

    setGuardando(true);
    setError("");
    const dto = {
      idNormativa,
      evaluadoPor: idUsuario,
      parametros: limpios.map((p) => ({
        parametro: p.parametro,
        cumple: p.cumple,
        justificacion: p.justificacion,
      })),
    };
    guardarEvaluacionImpacto(token, dto)
      .then(() => {
        setEditando(false);
        cargar();
        onGuardado?.(idNormativa);
      })
      .catch((e) => setError(typeof e === "string" ? e : "No se pudo guardar la evaluación."))
      .finally(() => setGuardando(false));
  };

  const parametrosMostrar = editando ? borrador : (data?.parametros || []);
  const puntajeMostrar = editando ? puntajeBorrador : (data?.puntaje ?? 0);
  const colorMostrar = editando ? colorBorrador : (data?.color || "Verde");
  const colorHex = colorHexFromNombre(colorMostrar);

  const mostrarAviso = !esAdmin && !editando;
  const esCopiaCliente = !!data?.esPersonalizadoCliente;

  if (loading) {
    return <Box display="flex" justifyContent="center" py={5}><CircularProgress /></Box>;
  }

  if (!data) {
    return (
      <>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
          No hay información de impacto para esta norma.
        </Typography>
      </>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {!esAdmin && !editando && (
        <Alert severity={esCopiaCliente ? "success" : "info"} sx={{ mb: 2 }}>
          {esCopiaCliente
            ? "Estás viendo la evaluación personalizada de tu organización."
            : "Estás viendo la evaluación base."}
        </Alert>
      )}

      <Box sx={{
        display: "flex", alignItems: "center", gap: 2, p: 2, mb: 2, borderRadius: 2,
        bgcolor: alpha(colorHex, 0.1), border: `1px solid ${alpha(colorHex, 0.3)}`,
      }}>
        <Box sx={{
          width: 56, height: 56, borderRadius: "50%", bgcolor: colorHex, color: "#fff",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.4rem", fontWeight: 700, flexShrink: 0,
        }}>
          {puntajeMostrar}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorHex }}>
            {nivelImpactoTexto(colorMostrar)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {puntajeMostrar} criterio(s) cumplido(s) · Bajo 0–1 · Medio 2–3 · Alto 4+
          </Typography>
        </Box>
        {!data.evaluado && !editando && (
          <Chip size="small" label="Sin evaluar" variant="outlined" sx={{ color: "text.disabled" }} />
        )}
        {puedeEditar && !editando && (
          <Button startIcon={<EditOutlinedIcon />} onClick={iniciarEdicion} size="small">
            Editar
          </Button>
        )}
      </Box>

      <Stack spacing={1.5}>
        {parametrosMostrar.map((p, idx) => {
          const cumple = p.cumple;
          const tituloEditable = editando && p.esLibre;
          return (
            <Box key={idx} sx={{
              p: 1.5, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}`,
              bgcolor: cumple ? alpha(theme.palette.success.main, 0.04) : "transparent",
            }}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                {editando ? (
                  <Switch size="small" checked={cumple} onChange={() => toggleCumple(idx)} sx={{ mt: -0.25 }} />
                ) : (
                  cumple
                    ? <CheckCircleIcon fontSize="small" sx={{ color: "success.main", mt: 0.25 }} />
                    : <RadioButtonUncheckedIcon fontSize="small" sx={{ color: "text.disabled", mt: 0.25 }} />
                )}
                <Box sx={{ flex: 1 }}>
                  {tituloEditable ? (
                    <TextField
                      fullWidth size="small" placeholder="Nombre del criterio"
                      value={p.pregunta || ""}
                      onChange={(e) => cambiarTitulo(idx, e.target.value)}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {p.pregunta || p.parametro}
                    </Typography>
                  )}

                  {editando ? (
                    <TextField
                      fullWidth size="small" multiline minRows={2}
                      placeholder="Justificación…"
                      value={p.justificacion || ""}
                      onChange={(e) => cambiarJustificacion(idx, e.target.value)}
                      sx={{ mt: 1 }}
                    />
                  ) : (
                    p.justificacion
                      ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}>
                          {p.justificacion}
                        </Typography>
                      : <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: "block" }}>
                          Sin justificación
                        </Typography>
                  )}

                  {!editando && p.evaluadoPor && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                      {p.evaluadoPor === "IA"
                        ? <AutoAwesomeIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                        : <PersonOutlineIcon sx={{ fontSize: 14, color: "text.disabled" }} />}
                      <Typography variant="caption" color="text.disabled">
                        {p.evaluadoPor === "IA" ? "Evaluado por IA" : "Editado manualmente"}
                        {p.fechaEvaluacion ? ` · ${formatFechaHora(p.fechaEvaluacion)}` : ""}
                      </Typography>
                    </Box>
                  )}
                </Box>
                {editando && (
                  <IconButton size="small" onClick={() => eliminarCriterio(idx)} sx={{ mt: -0.5 }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>

      {editando && (
        <Button startIcon={<AddIcon />} onClick={agregarCriterio} size="small" sx={{ mt: 1.5 }}>
          Agregar criterio
        </Button>
      )}

      {editando && (
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
          <Button onClick={cancelarEdicion} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? <CircularProgress size={20} color="inherit" /> : "Guardar"}
          </Button>
        </Stack>
      )}

      {mostrarAviso && (
        <Paper
          elevation={0}
          sx={{
            mt: 2.5, p: 1.75, display: "flex", gap: 1.25, alignItems: "flex-start",
            bgcolor: alpha(theme.palette.info.main, 0.06),
            border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
            borderRadius: 1.5,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "info.main", mt: 0.15, flexShrink: 0 }} />
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Los criterios de impacto constituyen una herramienta de carácter meramente referencial y preliminar,
            destinada a facilitar una primera aproximación a la evaluación normativa. En ningún caso
            sus resultados deben entenderse como una asesoría legal, ni sustituyen el análisis particular
            que corresponde efectuar a un equipo jurídico especializado.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default CriteriosImpactoContenido;